import i18n from '../i18n';
import { isChordToken } from './chordBars';
import { getChordNotes, parseChord } from './chordNotes';
import { FLAT_SCALE, SHARP_SCALE, getChromaticScale, getDiatonicChords, getRootNote, isMinorKey } from './diatonicChords';
import { getKeyPitchClass } from './keyDistance';

/**
 * Every chord the Chord Library sheet offers for a key: the key's own chords, the ones borrowed
 * from the scales built on the same tonic, slash chords, and the colours (7ths, sus, add9, 6) that
 * stay inside the key.
 *
 * A borrowed chord is marked `isCommon` when it is one of the key's own, so the sheet can tell the
 * chords a mode actually adds apart from the ones it shares.
 */

const MAJOR_STEPS = [0, 2, 4, 5, 7, 9, 11];

/** The modes on the song's tonic, in the order the sheet lists them. Ionian and Aeolian are the
 * key and its parallel, so only one of the pair is ever a row of its own. Named under `chordLibrary.modes`. */
const MODES = [
  { id: 'ionian', steps: MAJOR_STEPS },
  { id: 'dorian', steps: [0, 2, 3, 5, 7, 9, 10] },
  { id: 'phrygian', steps: [0, 1, 3, 5, 7, 8, 10] },
  { id: 'lydian', steps: [0, 2, 4, 6, 7, 9, 11] },
  { id: 'mixolydian', steps: [0, 2, 4, 5, 7, 9, 10] },
  { id: 'aeolian', steps: [0, 2, 3, 5, 7, 8, 10] },
  { id: 'locrian', steps: [0, 1, 3, 5, 6, 8, 10] },
];

const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const NATURALS = [0, 2, 4, 5, 7, 9, 11];
const ROMANS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];

const TRIAD_SUFFIX = { major: '', minor: 'm', dim: 'dim' };

/** Slash chords that work in the key, as [chord degree, bass degree] from its own scale. */
const MAJOR_SLASH = [
  [0, 2], // C/E
  [0, 4], // C/G
  [0, 6], // C/B
  [1, 3], // Dm/F
  [3, 5], // F/A
  [3, 0], // F/C
  [3, 4], // F/G
  [4, 6], // G/B
  [4, 3], // G/F
  [5, 4], // Am/G
];

const MINOR_SLASH = [
  [0, 2], // Cm/Eb
  [0, 4], // Cm/G
  [0, 6], // Cm/Bb
  [2, 4], // Eb/G
  [3, 5], // Fm/Ab
  [3, 0], // Fm/C
  [4, 6], // Gm/Bb
  [5, 0], // Ab/C
  [5, 6], // Ab/Bb
  [6, 1], // Bb/D
  [6, 5], // Bb/Ab
];

/** Colours a chord of the key can take without leaving it — 5ths are power chords, root and fifth
 * only. The first variant that fits wins. Named under `chordLibrary.extensions`. */
const EXTENSIONS = [
  {
    id: 'sevenths',
    variants: [
      { on: 'major', suffix: 'M7', steps: [0, 4, 7, 11] },
      { on: 'major', suffix: '7', steps: [0, 4, 7, 10] },
      { on: 'minor', suffix: 'm7', steps: [0, 3, 7, 10] },
    ],
  },
  {
    id: 'fifths',
    variants: [
      { on: 'major', suffix: '5', steps: [0, 7] },
      { on: 'minor', suffix: '5', steps: [0, 7] },
    ],
  },
  {
    id: 'sus2',
    variants: [
      { on: 'major', suffix: 'sus2', steps: [0, 2, 7] },
      { on: 'minor', suffix: 'sus2', steps: [0, 2, 7] },
    ],
  },
  {
    id: 'sus4',
    variants: [
      { on: 'major', suffix: 'sus4', steps: [0, 5, 7] },
      { on: 'minor', suffix: 'sus4', steps: [0, 5, 7] },
    ],
  },
  {
    id: 'add9',
    variants: [
      { on: 'major', suffix: 'add9', steps: [0, 4, 7, 2] },
      { on: 'minor', suffix: 'madd9', steps: [0, 3, 7, 2] },
    ],
  },
  {
    id: 'sixths',
    variants: [
      { on: 'major', suffix: '6', steps: [0, 4, 7, 9] },
      { on: 'minor', suffix: 'm6', steps: [0, 3, 7, 9] },
    ],
  },
];

/**
 * Chromatic chords that lead into a chord of the key: a diminished 7th a half step under its
 * target, or an augmented chord pulling up into it. As [root degree, raised, quality, target degree].
 */
const MAJOR_PASSING = [
  [0, true, 'dim7', 1], // C#dim7 → Dm
  [1, true, 'dim7', 2], // D#dim7 → Em
  [3, true, 'dim7', 4], // F#dim7 → G
  [4, true, 'dim7', 5], // G#dim7 → Am
  [0, false, 'aug', 3], // Caug → F
  [4, false, 'aug', 0], // Gaug → C
];

const MINOR_PASSING = [
  [6, true, 'dim7', 0], // Bdim7 → Cm
  [3, true, 'dim7', 4], // F#dim7 → Gm
  [2, false, 'aug', 3], // Ebaug → Fm
  [0, false, 'aug', 3], // Caug → Fm
];

const mod12 = (n) => ((n % 12) + 12) % 12;

const modeLabel = (id) => i18n.t(`chordLibrary.modes.${id}`);

/**
 * A note the rest of the app can play. Playback and the piano only know the twelve sharp and flat
 * names, so B#, E#, Cb and Fb — right by letter, silent in practice — take the key's own spelling.
 */
const playable = (note, chromatic) =>
  SHARP_SCALE.includes(note) || FLAT_SCALE.includes(note) ? note : chromatic[mod12(getKeyPitchClass(note))];

const triadQuality = (third, fifth) => {
  if (third === 4 && fifth === 7) return 'major';
  if (third === 3 && fifth === 7) return 'minor';
  if (third === 3 && fifth === 6) return 'dim';

  return null;
};

/**
 * The seven notes of a mode on the key's tonic, spelled one letter per degree — so C Phrygian is
 * Db Eb Ab Bb rather than the sharps C major's chromatic scale would give. A degree that would
 * need a double accidental falls back to the key's own chromatic spelling.
 */
const spellMode = (key, steps) => {
  const tonic = getRootNote(key);
  const tonicPc = getKeyPitchClass(tonic);
  const letterIndex = LETTERS.indexOf(tonic[0]);
  const chromatic = getChromaticScale(key);

  return steps.map((step, degree) => {
    const pc = mod12(tonicPc + step);
    const letter = (letterIndex + degree) % 7;
    const offset = mod12(pc - NATURALS[letter] + 6) - 6;
    const accidental = { '-1': 'b', 0: '', 1: '#' }[offset];

    return accidental === undefined ? chromatic[pc] : playable(`${LETTERS[letter]}${accidental}`, chromatic);
  });
};

/**
 * The seven notes of a key's own scale, major or natural minor, spelled one letter per degree.
 *
 * @param {string} key - A key with one spelling, e.g. 'Db' or 'C#m' — a pair would leave the
 *   choice of tonic to getRootNote
 * @returns {string[]}
 */
export const getKeyScaleNotes = (key) =>
  spellMode(key, isMinorKey(key) ? MODES.find((mode) => mode.id === 'aeolian').steps : MAJOR_STEPS);

const romanFor = (degree, step, quality) => {
  const shift = step - MAJOR_STEPS[degree];
  const accidental = { '-1': '♭', 0: '', 1: '♯' }[shift] ?? '';
  const numeral = quality === 'major' ? ROMANS[degree] : ROMANS[degree].toLowerCase();

  return `${accidental}${numeral}${quality === 'dim' ? '°' : ''}`;
};

/** The seven triads of a mode, with the quality of each read off the mode itself. */
const modeTriads = (key, steps) => {
  const notes = spellMode(key, steps);

  return steps.map((step, degree) => {
    const third = mod12(steps[(degree + 2) % 7] - step);
    const fifth = mod12(steps[(degree + 4) % 7] - step);
    const quality = triadQuality(third, fifth);

    return {
      name: `${notes[degree]}${TRIAD_SUFFIX[quality]}`,
      roman: romanFor(degree, step, quality),
      pc: mod12(getKeyPitchClass(notes[degree])),
      quality,
    };
  });
};

const baseQuality = (name) => {
  if (/dim$/.test(name)) return 'dim';

  return /m$/.test(name) ? 'minor' : 'major';
};

const rootOf = (name) => name.replace(/(m|dim)$/, '');

/** The key's own seven pitch classes, read off its chords' roots. */
const keyPitchClasses = (common) => new Set(common.map((chord) => getKeyPitchClass(chord.name)));

/** The mode rows in the order the sheet lists them: the parallel, then the rest, never the key's own. */
const modeOrder = (minor) => {
  const own = minor ? 'aeolian' : 'ionian';
  const parallel = minor ? 'ionian' : 'aeolian';

  return [parallel, ...MODES.map((mode) => mode.id).filter((id) => id !== own && id !== parallel)];
};

/**
 * @param {string} key - The song's key, in any spelling the app stores
 * @returns {{
 *   common: {name: string, roman: string}[],
 *   scales: {id: string, label: string, title: string, chords: {name: string, roman: string, isCommon: boolean}[]}[],
 *   slash: {name: string}[],
 *   extensions: {id: string, label: string, chords: {name: string}[]}[],
 *   passing: {name: string, roman: string}[],
 * }|null} null when the key can't be read
 */
export function getChordLibrary(key) {
  const common = getDiatonicChords(key);

  if (common.length !== 7) return null;

  const minor = isMinorKey(key);
  const tonic = getRootNote(key);
  const commonByPitch = new Map(
    common.map((chord) => [`${getKeyPitchClass(chord.name)}:${baseQuality(chord.name)}`, chord.name]),
  );
  const scalePcs = keyPitchClasses(common);
  const parallel = minor ? 'ionian' : 'aeolian';
  const order = modeOrder(minor);

  const scales = order.map((id) => {
    const mode = MODES.find((entry) => entry.id === id);
    const chords = modeTriads(key, mode.steps).map(({ name, roman, pc, quality }) => {
      // A shared chord is written the way the key's own row writes it, so the two read as one chord
      const shared = commonByPitch.get(`${pc}:${quality}`);

      return { name: shared ?? name, roman, isCommon: Boolean(shared) };
    });

    if (id === parallel) {
      const title = i18n.t(minor ? 'chordLibrary.major' : 'chordLibrary.minor', { tonic });

      return { id, label: i18n.t('chordLibrary.parallel'), title, chords };
    }

    const label = modeLabel(id);

    return { id, label, title: i18n.t('chordLibrary.modeTitle', { tonic, mode: label }), chords };
  });

  const roots = common.map((chord) => rootOf(chord.name));
  const slash = (minor ? MINOR_SLASH : MAJOR_SLASH)
    .map(([chord, bass]) => ({ name: `${common[chord].name}/${roots[bass]}` }))
    .filter(({ name }) => isChordToken(name));

  const extensions = EXTENSIONS.map(({ id, variants }) => ({
    id,
    label: i18n.t(`chordLibrary.extensions.${id}`),
    chords: common
      .map((chord) => {
        const root = rootOf(chord.name);
        const rootPc = getKeyPitchClass(root);
        const fit = variants.find(
          ({ on, steps }) =>
            on === baseQuality(chord.name) && steps.every((step) => scalePcs.has(mod12(rootPc + step))),
        );

        return fit ? { name: `${root}${fit.suffix}` } : null;
      })
      .filter((chord) => chord && isChordToken(chord.name)),
  }));

  const chromatic = getChromaticScale(key);
  const passing = (minor ? MINOR_PASSING : MAJOR_PASSING)
    .map(([degree, raised, quality, target]) => {
      const root = roots[degree];
      const sharp = root.endsWith('b') ? root.slice(0, -1) : `${root}#`;
      // A root that is already sharp would need a double sharp, so it takes the key's spelling
      const raisedRoot = root.endsWith('#') ? chromatic[mod12(getKeyPitchClass(root) + 1)] : playable(sharp, chromatic);

      return { name: `${raised ? raisedRoot : root}${quality}`, roman: `→ ${common[target].name}` };
    })
    .filter(({ name }) => isChordToken(name));

  return { common, scales, slash, extensions, passing };
}

/**
 * The name the library's legend gives a family for a key, in the app's language — 'Parallel' for the
 * key's parallel scale.
 *
 * @param {string} key - The song's key
 * @param {string} family - A getChordFamily answer other than 'key'
 * @returns {string|null}
 */
export function getFamilyLabel(key, family) {
  if (family === 'chromatic') return i18n.t('chordLibrary.chromatic');
  if (family === (isMinorKey(key) ? 'ionian' : 'aeolian')) return i18n.t('chordLibrary.parallel');

  return MODES.some((mode) => mode.id === family) ? modeLabel(family) : null;
}

const familyCache = new Map();

/** Every pitch class a chord sounds, its bass included, or null when it can't be read. */
const chordPitchClasses = (chord) => {
  const parsed = parseChord(chord);
  const rootPc = parsed && getKeyPitchClass(parsed.root);

  if (rootPc == null) return null;

  const pcs = getChordNotes(chord).intervals.map((step) => mod12(rootPc + step));
  const bassPc = parsed.bassNote ? getKeyPitchClass(parsed.bassNote) : null;

  return bassPc == null ? pcs : [...pcs, bassPc];
};

/** Builds the lookup for one key: its own notes, then each mode's in the sheet's order. */
const familyLookup = (key) => {
  const common = getDiatonicChords(key);

  if (common.length !== 7) return null;

  const tonicPc = getKeyPitchClass(getRootNote(key));
  const modes = modeOrder(isMinorKey(key)).map((id) => ({
    id,
    pcs: new Set(MODES.find((mode) => mode.id === id).steps.map((step) => mod12(tonicPc + step))),
  }));

  return { own: keyPitchClasses(common), modes, chords: new Map() };
};

/**
 * Where a chord sits in the Chord Library for a key: 'key' when every note is in the key, else the
 * first mode row that holds all of them, else 'chromatic' (secondary dominants, passing dim7/aug).
 *
 * @param {string} key - The song's key
 * @param {string} chord - A chord symbol as written in a chart or over a lyric
 * @returns {string|null} 'key', a mode id, 'chromatic', or null when the key or chord can't be read
 */
export function getChordFamily(key, chord) {
  if (!key || !chord) return null;

  if (!familyCache.has(key)) familyCache.set(key, familyLookup(key));

  const lookup = familyCache.get(key);

  if (!lookup) return null;
  if (lookup.chords.has(chord)) return lookup.chords.get(chord);

  const pcs = chordPitchClasses(chord);
  let family = null;

  if (pcs) {
    const fits = (set) => pcs.every((pc) => set.has(pc));

    family = fits(lookup.own) ? 'key' : lookup.modes.find((mode) => fits(mode.pcs))?.id ?? 'chromatic';
  }

  lookup.chords.set(chord, family);

  return family;
}
